import axios from 'axios';
import { keysToCamelCaseDeep } from '@agro1desenvolvimento/utils-js';
import Yup from '../utils/yup';
import {
  parseRequest,
  parseResponse,
  toSnakePayload,
} from '../utils/schema-parse';
import {
  RelatorioVisita,
  relatorioVisitaSchema,
} from '../features/relatorio-visita/schemas/payload.schema';
import aqilaApi from './aqila-api';
import ApiGlobalService from './api-global';
import LocalStorageService from './local-storage';

export type RelatorioVisitaToSave = {
  propriedadeId: number;
  safraId: number;
  data: string;
  glebaIds: number[];
  situacaoGeral?: string | null;
  proximaVisita?: string | null;
};

export type RelatorioVisitaCreated = {
  id: number;
  uuid: string;
  urlAcesso: string;
};

export type RelatorioVisitaUsuario = {
  id: number;
  uuid: string;
  nome: string;
  nomeCompleto: string;
  email: string;
  cor: string;
};

export type RelatorioVisitaListItem = {
  id: number;
  data: string;
  createdAt: string;
  uuid: string;
  tipo: string;
  usuario: RelatorioVisitaUsuario | null;
};

export type RelatorioVisitaListParams = {
  propriedade?: number;
  safra?: number;
  page?: number;
  perPage?: number;
};

const createSchema = Yup.object({
  propriedadeId: Yup.number().required(),
  safraId: Yup.number().required(),
  data: Yup.string().required(),
  glebaIds: Yup.array().of(Yup.number().required()).min(1).required(),
  situacaoGeral: Yup.string().nullable(),
  proximaVisita: Yup.string().nullable(),
});

class RelatorioVisitaService {
  private readonly endpoint = 'v2/relatorio_visita_publico';

  async create(record: RelatorioVisitaToSave): Promise<RelatorioVisitaCreated> {
    const relatorio = parseRequest(createSchema, record);

    const { data } = await aqilaApi.post<RelatorioVisitaCreated>(
      this.endpoint,
      toSnakePayload({ relatorio }),
    );

    return data;
  }

  async list(
    params: RelatorioVisitaListParams = {},
  ): Promise<RelatorioVisitaListItem[]> {
    if (params.page !== undefined) {
      const { data } = await aqilaApi.get<RelatorioVisitaListItem[]>(
        'v2/relatorio_visita',
        { params: { tipo: 'publico', ...params } },
      );

      return data ?? [];
    }

    const perPage = params.perPage ?? 100;

    const fetchFrom = async (
      page: number,
      acc: RelatorioVisitaListItem[],
    ): Promise<RelatorioVisitaListItem[]> => {
      const { data } = await aqilaApi.get<RelatorioVisitaListItem[]>(
        'v2/relatorio_visita',
        { params: { tipo: 'publico', ...params, page, perPage } },
      );

      const batch = data ?? [];
      const all = acc.concat(batch);

      return batch.length === perPage ? fetchFrom(page + 1, all) : all;
    };

    return fetchFrom(1, []);
  }

  async findPublic(uuid: string): Promise<RelatorioVisita> {
    const baseUrl = await this.resolveApiBaseUrl();

    const { data } = await axios.get<Record<string, unknown>>(
      `${baseUrl}/${this.endpoint}/${uuid}`,
    );

    return parseResponse(relatorioVisitaSchema, keysToCamelCaseDeep(data));
  }

  async operacaoDatas(params: {
    propriedade: number;
    safra?: number;
    tipo?: string;
  }): Promise<string[]> {
    const { data } = await aqilaApi.get<string[]>('v1/operacoes_campo/datas', {
      params,
    });

    return data ?? [];
  }

  private async resolveApiBaseUrl(): Promise<string> {
    if (!LocalStorageService.getItem('apiHostUrl')) {
      await ApiGlobalService.inicializeApiHostUrl();
    }

    const baseUrl = LocalStorageService.getItem('apiHostUrl');

    if (!baseUrl) {
      throw new Error('Não foi possível resolver a URL da API.');
    }

    return baseUrl.replace(/\/+$/, '');
  }
}

export default new RelatorioVisitaService();
